import React, { ReactNode } from 'react';
import { GridChildComponentProps, VariableSizeGrid } from 'react-window';
import { Button, Table, Tooltip } from 'antd';
import Icon, { LoadingOutlined } from '@ant-design/icons';
import AutoSizer from 'react-virtualized-auto-sizer';
import cx from 'classnames';

import { SortButton, SortStatus } from '../sortButton';
import { TableSort } from '../../../utils/dataTable/data';
import { ExtendedColumnProps } from '../../../utils/dataTable/column';
import { IAccountDetails, IUser } from '../../../slices/users/user';
import { ReactComponent as EditOutlined } from '../../../assets/sx-edit.svg';
import { UserTypeMap, UserTypes } from '../domain/userTypes';
import { ScrollPosition } from '../../../slices/utils';
import { UserRoles } from '../domain/userRoles';
import { ACCOUNT_ACCESS } from './interfaces';
import { Features, isFeatureEnabled } from '../../../featureToggles/features';
import { INVOICE_NUMBER, isInvoice } from '../../../slices/accounts/paymentAndBilling/types';
import { AccessTypes } from '../domain/accountAccessType';

import styles from './dataTable.plgexternal.module.scss';

const isPlgExternalEnv = isFeatureEnabled(Features.PlgExternal3);

export enum EntityHandlers {
  AccessPermissions = 'EDIT_ACCESS_PERMISSIONS'
}

interface IProps<T extends IDataTableEntity> {
  columns: ExtendedColumnProps<T>[];
  data: T[];
  loading: boolean;
  editingEntity: string;
  withActiveToggle: boolean;
  toggleEditEntityHandler: (isOpen: boolean, entityToEdit: T, handler: EntityHandlers | null) => void;
  onToggleActiveState?: (entityToEdit: T) => void;
  sortTable: (payload: TableSort) => void;
  sort: TableSort;
  loggedInUser: IUser | null;
  initialScroll?: ScrollPosition;
  noDataMessage?: ReactNode;
  logEvent?: (recordNumber: string) => void;
}

interface IDataTableEntity {
  id: string | number;
  isActive?: boolean;
  status?: string;
  account?: IAccountDetails;
  // use for Features.PlgExternal
  roles?: Array<string>;
  userType?: UserTypes;
  // end of use for Features.PlgExternal
}

interface CustomizeScrollBodyinfo {
  scrollbarSize: number;
  ref: React.Ref<{
    scrollLeft: number;
  }>;
  onScroll: (info: {
    currentTarget?: HTMLElement;
    scrollLeft?: number;
  }) => void;
}

const actionsColumnWidth = 48;
const nameColumnWidth = 184;

const isPlgExternal3Env = isFeatureEnabled(Features.PlgExternal3);

export class DataTable<T extends IDataTableEntity> extends React.Component<IProps<T>> {
  constructor(props: IProps<T>) {
    super(props);

    this.renderVirtualList = this.renderVirtualList.bind(this);
    this.handleOptionClick = this.handleOptionClick.bind(this);
  }

  componentDidUpdate(prevProps: IProps<T>): void {
    this.restoreScrollHeight();
  }

  private restoreScrollHeight(): void {
    const { initialScroll } = this.props;
    const tableContainer = document.getElementsByClassName('ant-table-content');
    if ((!initialScroll?.x && !initialScroll?.y) || !tableContainer.length) {
      return;
    }
    tableContainer[0].scrollTo(initialScroll.x, initialScroll.y);
  }

  private onSort(column: ExtendedColumnProps<T>, sortStatus: SortStatus): void {
    this.props.sortTable({
      key: column.key,
      status: sortStatus
    });
  }

  private renderActionsColumn(_value: unknown, record: T): ReactNode {
    return (
      <div className={styles.actionsContainer}>
        {this.renderEditAction(_value, record)}
      </div>
    );
  }

  private renderNameColumn(_value: unknown, record: T): ReactNode {
    return this.renderEditAction(_value, record);
  }

  private renderEditAction(_value: unknown, record: T): ReactNode {
    const { loggedInUser } = this.props;
    if (!this.props.toggleEditEntityHandler) {
      return null;
    }
    let message = '';
    let shouldDisable = false;
    if (loggedInUser && loggedInUser.userType === UserTypes.User && record.id !== loggedInUser.id) {
      message = 'You don\'t have the permissions to perform this action';
      shouldDisable = true;
    }

    let iconIU = isPlgExternal3Env ? _value : <Icon
      component={EditOutlined}
      className={styles.editIcon}
      disabled={shouldDisable}
    />;

    if (shouldDisable) {
      iconIU = <Tooltip title={message}>
        {iconIU}
      </Tooltip>;
    }

    return (
      <div className={cx(styles.editIconContainer, { [styles.nameContainer]: isPlgExternal3Env })}
        onClick={shouldDisable ? undefined : this.props.toggleEditEntityHandler.bind(this, true, record, null)}
      >
        {iconIU}
      </div>
    );
  }

  private renderDataWithActions(value: unknown, record: T): ReactNode {
    const { loggedInUser, toggleEditEntityHandler } = this.props;
    if (!toggleEditEntityHandler) {
      return null;
    }
    const shouldDisable = loggedInUser && loggedInUser.userType === UserTypes.User && record.id !== loggedInUser.id;
    const message = shouldDisable ? 'You don\'t have the permissions to perform this action' : '';

    return (
      <div className={cx(styles.clickableCell, { [styles[Features.PlgExternal3]]: isPlgExternalEnv })}
        onClick={shouldDisable ? undefined : toggleEditEntityHandler.bind(this, true, record, null)}
      >
        <Tooltip title={message}>
          {value}
        </Tooltip>
      </div>
    );
  }

  private handleOptionClick(recordNumber: string) {
    const { logEvent } = this.props;
    if (logEvent) {
      logEvent(recordNumber);
    }
  }

  private renderInvoiceNumberColumn(_value: unknown, record: T): ReactNode {
    if (!isPlgExternalEnv || !isInvoice(record)) {
      return null;
    }
    const pdfUrl = record.pdfUrl ?? '';
    const invoiceNumber = record.number ?? '';
    return (
      <a
        target='blank'
        href={pdfUrl}
        className={cx(styles.invoiceNumber, { [styles[Features.PlgExternal3]]: isPlgExternalEnv })}
        onClick={() => this.handleOptionClick(record.number ?? '')}
      >
        {invoiceNumber}
      </a>
    );
  }

  private renderAccountAccessColumn(_value: unknown, record: T): ReactNode {
    const userRoles = record.roles ?? [];
    const isOwner = record.account?.accessType !== AccessTypes.Enterprise && !!userRoles.find((role) => role === UserRoles.CompanyOwner);
    const userType = UserTypeMap.get(record.userType || UserTypes.User);
    return <div className={cx(styles.accessBubble, {
      [styles.isOwner]: isOwner
    })}>{isOwner ? 'Owner' : userType}</div>;
  }

  private renderAccessColumn(_value: unknown, record: T): ReactNode {
    const { toggleEditEntityHandler } = this.props;
    return <div className={styles.buttonContainer}>
      <Button
        className={styles.button}
        type='link'
        onClick={() => toggleEditEntityHandler(true, record, EntityHandlers.AccessPermissions)}
      >
        Edit access
      </Button>
    </div >;
  }

  private renderIsActiveToggle(_value: unknown, record: T): ReactNode {
    const { editingEntity, withActiveToggle, loggedInUser } = this.props;

    if (
      !withActiveToggle
      || (loggedInUser && loggedInUser.userType === UserTypes.User)
    ) {
      return null;
    }

    if (editingEntity && editingEntity === record.id) {
      return <LoadingOutlined />;
    }
    const isActive = record.status === 'active';
    const switchUI = <div
      className={cx(styles.activeToggle, {
        [styles.inactive]: !isActive
      })}
    >
      <span className={styles.circle} />{isActive ? 'Active' : 'Lapsed'}
    </div>;

    return <div>{switchUI}</div>;
  }

  private renderColumnSorter(colDefinition: ExtendedColumnProps<T>): ReactNode {
    if (!colDefinition.sorter) {
      return null;
    }
    const { sort } = this.props;
    const sorterDefinition = {
      ...colDefinition,
      key: colDefinition.sorterKey || colDefinition.key
    };
    const status = sort.key === sorterDefinition.key ? sort.status : SortStatus.None;
    return <SortButton
      status={status}
      onSort={this.onSort.bind(this, sorterDefinition)}
      noSortClassName={cx(styles.sortIcon, styles.disabled)}
      sortDownClassName={styles.sortIcon}
      sortUpClassName={styles.sortIcon}
    />;
  }

  private renderColumnTitle(colDefinition: ExtendedColumnProps<T>): ReactNode {
    return <div className={cx(styles.titleContainer, {
      [styles.actionsColumnTitle]: colDefinition.isActionsColumn
    })}>
      <div className={cx(styles.columnTitle)}>{colDefinition.title}</div>
      {this.renderColumnSorter(colDefinition)}
    </div>;
  }

  private renderColumnData(rawData: T[], { columnIndex, rowIndex, style }: GridChildComponentProps) {
    const { columns = [] } = this.props;
    const record: T = rawData[rowIndex];
    const columnDataIndex = columns[columnIndex].dataIndex as keyof T;

    if (!(columnDataIndex in record)) {
      return null;
    }

    return <div style={style} key={`r_${rowIndex}_c_${columnIndex}`}>
      {record[columnDataIndex]}
    </div>;
  }

  private getColumnWidth(columnIndex: number, totalWidth: number, columnsCount: number): number {
    const lastColumnIndex = columnsCount - 1;
    return columnIndex === lastColumnIndex ? actionsColumnWidth : (totalWidth - actionsColumnWidth) / (lastColumnIndex);
  }

  private renderVirtualList(rawData: T[], { onScroll }: CustomizeScrollBodyinfo): ReactNode {
    const { columns = [] } = this.props;
    return <AutoSizer>
      {({ height, width }) => (
        <VariableSizeGrid
          columnCount={columns.length}
          columnWidth={(index) => this.getColumnWidth(index, width, columns.length)}
          rowCount={rawData.length}
          rowHeight={() => 54}
          height={height}
          width={width}
          onScroll={({ scrollLeft }) => {
            onScroll({ scrollLeft });
          }}
        >
          {this.renderColumnData.bind(this, rawData)}
        </VariableSizeGrid>
      )}
    </AutoSizer>;
  }

  private getLocaleConfiguration() {
    const { noDataMessage } = this.props;
    return noDataMessage ? { emptyText: noDataMessage } : undefined;
  }

  render(): ReactNode {
    const { columns = [], data = [], loading, editingEntity } = this.props;
    const columnsWithHandlers = columns.map(col => {
      let renderMethod = col.render;

      if (col.mapper) {
        renderMethod = col.mapper;
        if (col.render) {
          renderMethod = (value, ...args) => col.render?.(col.mapper?.(value), ...args);
        }
      }

      if (col.key === INVOICE_NUMBER) {
        renderMethod = this.renderInvoiceNumberColumn.bind(this);
      }

      if (col.key === ACCOUNT_ACCESS) {
        renderMethod = this.renderAccountAccessColumn.bind(this);
      }

      if (isFeatureEnabled(Features.SectionsPermissions) && col.key === 'access') {
        renderMethod = this.renderAccessColumn.bind(this);
      }

      if (col.key === 'accountStatus') {
        renderMethod = this.renderIsActiveToggle.bind(this);
      }

      if (col.isActionsColumn) {
        renderMethod = this.renderActionsColumn.bind(this);
      }

      if (col.showDataWithAction && isPlgExternalEnv) {
        renderMethod = this.renderDataWithActions.bind(this);
      }

      let width = col.isActionsColumn ? `${actionsColumnWidth}px` : undefined;
      if (isPlgExternalEnv && col.autoWidth) {
        width = undefined;
      }

      if (col.isClickableColumn && isFeatureEnabled(Features.PlgExternal3)) {
        renderMethod = this.renderNameColumn.bind(this);
        width = `${nameColumnWidth}px`;
      }

      return {
        ...col,
        title: this.renderColumnTitle.bind(this, col),
        render: renderMethod,
        sorter: undefined,
        width: width,
        className: cx(styles.dataTableCell, {
          [styles.rightActionCell]: col.isActionsColumn,
          [styles.autoWidth]: col.autoWidth && isPlgExternalEnv
        })
      };
    });

    if (loading && !editingEntity) {
      return <div className={styles.loadingContainer}>
        <LoadingOutlined className={styles.loadingSpin} />
      </div>;
    }

    return <Table<T>
      columns={columnsWithHandlers}
      rowKey="id"
      dataSource={data}
      pagination={false}
      scroll={{ x: 'max-content' }}
      components={{ body: this.renderVirtualList }}
      className={styles.dataTable}
      locale={this.getLocaleConfiguration()}
    />;
  }
}
